import { type CSSProperties, useLayoutEffect, useRef, useState } from "react";
// Video.js plugins and components register on import, before the first videojs() call.
import "@/common/videojs/ResolutionSwitcher";
import "@/common/videojs/TimelineMarkers";
import "@/common/videojs/ChainNavButton";
import "@/common/videojs/CenteredTimeTooltip";
import { statusTypes } from "@/modules/ExperienceData";
import Watermark from "@/components/player/Watermark";
import { useChainNavigation } from "@/components/player/useChainNavigation";
import { useVideoFullscreen } from "@/components/player/useVideoFullscreen";
import { useVideoTracking } from "@/components/player/useVideoTracking";
import { usePlayerSource } from "@/components/player/usePlayerSource";
import { usePlayerEvents } from "@/components/player/usePlayerEvents";
import { useLandscapeRecomposite } from "@/components/player/useLandscapeRecomposite";
import { usePlayerCreation } from "@/components/player/usePlayerCreation";
import {
  createVideoJsSelf,
  templateSizes,
  type VideoJsProps,
} from "@/components/player/videoJsTypes";
import type { ExperienceState } from "@/types/state";

function getWatermark(experienceState: ExperienceState) {
  const experienceData = experienceState.experienceData;
  let title: string | undefined;
  // biome-ignore lint/suspicious/noDoubleEquals: the status comes from the unchecked API payload; the loose comparison is the original one
  if (experienceData.getStatus() == statusTypes.DEMO) {
    title = "DEMO";
  } else if (
    // biome-ignore lint/suspicious/noDoubleEquals: the status comes from the unchecked API payload; the loose comparison is the original one
    experienceData.getStatus() == statusTypes.DRAFT
  ) {
    title = "DRAFT";
  }

  return <Watermark experienceState={experienceState} title={title} />;
}

export default function VideoJs(props: VideoJsProps) {
  const [self] = useState(() => createVideoJsSelf(props));
  const chain = useChainNavigation(self);
  const fullscreen = useVideoFullscreen(self);
  const tracking = useVideoTracking(self);
  const source = usePlayerSource(self, chain, tracking);
  const events = usePlayerEvents(self, tracking, fullscreen, chain, source);
  const recomposite = useLandscapeRecomposite(self);
  const lifecycle = usePlayerCreation(
    self,
    tracking,
    fullscreen,
    source,
    events,
    recomposite,
  );
  const mounted = useRef(false);

  // The handlers read the props of the last commit, as the class read this.props.
  useLayoutEffect(() => {
    self.props = props;
  });

  // componentDidMount / componentWillUnmount. A layout effect keeps the class
  // commit-phase order: mount runs before Experience's mount, and on deletion
  // Experience's cleanup (registry dispose) runs before this one.
  useLayoutEffect(() => {
    lifecycle.mount();
    return lifecycle.unmount;
  }, [lifecycle]);

  // componentDidUpdate.
  useLayoutEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    lifecycle.update();
  });

  const experienceData = props.experienceState.experienceData;
  const size = templateSizes(props.experienceState).player.video;

  const watermark =
    experienceData.isLoaded() &&
    // biome-ignore lint/suspicious/noDoubleEquals: the status comes from the unchecked API payload; the loose comparison is the original one
    experienceData.getStatus() != statusTypes.PUBLISH
      ? getWatermark(props.experienceState)
      : null;

  return (
    <div
      className="video-panel"
      // `marginBottm` is not a CSS property, so the bottom margin never applies.
      style={
        {
          marginLeft: size.margins.left,
          marginTop: size.margins.top,
          marginBottm: size.margins.bottom,
        } as CSSProperties
      }
    >
      {/* React-owned but ALWAYS-EMPTY host. The mount creates the <video>
          imperatively INTO this node (fresh-player path) or re-hosts the
          persistent player's live `.video-js` node here (rotate adopt path).
          Because React never renders children into it, React never reconciles /
          removes the relocated media element across a rotate remount — that is
          what lets the registry move the live player without React fighting it.
          `.video-host { display: contents }` keeps it layout-neutral, so the
          `.video-js` sizes exactly as before in both orientations. */}
      <div
        className="video-host"
        ref={e => {
          self.videoHost = e;
        }}
      />
      {watermark}
    </div>
  );
}
